import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import './projectOverview.scss';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { PROJECT_OVERVIEW } from '../../../Api/resources/endPoint';
import { getExportFileName, getExportFileNameWithDate } from '../utils';
import UseAxios from '../../../Api/http/http';
import { getArchitectNamesData } from '../../../Api/request/projectOverviewMasterData';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DISPLAY_DATE_WITH_COMMA = /^(\d{1,2})\s+([A-Za-z]{3,}),\s+(\d{4})$/;
const DISPLAY_DATE_WITH_DASH = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DISPLAY_DATE_PREFIX = /^(\d{1,2}\s+[A-Za-z]{3,},\s+\d{4})(\s*:.*)$/;

const normalizeMonthToken = (monthValue = '') => {
  const monthPrefix = String(monthValue).trim()
    .slice(0, 3);
  if (!monthPrefix) return '';

  const normalized = monthPrefix.charAt(0).toUpperCase() + monthPrefix.slice(1).toLowerCase();
  return MONTHS.includes(normalized) ? normalized : '';
};

const normalizeOverviewDisplayDate = (inputDate) => {
  const raw = String(inputDate ?? '').trim();
  if (!raw) return raw;

  const prefixedMatch = raw.match(DISPLAY_DATE_PREFIX);
  if (prefixedMatch) {
    const [, dateText, suffix] = prefixedMatch;
    const normalizedDate = normalizeOverviewDisplayDate(dateText);
    return normalizedDate ? `${normalizedDate}${suffix}` : raw;
  }

  const commaMatch = raw.match(DISPLAY_DATE_WITH_COMMA);
  if (commaMatch) {
    const [, day, month, year] = commaMatch;
    const normalizedMonth = normalizeMonthToken(month);
    return normalizedMonth ? `${String(Number(day)).padStart(2, '0')}-${normalizedMonth}-${year}` : raw;
  }

  const dashedMatch = raw.match(DISPLAY_DATE_WITH_DASH);
  if (dashedMatch) {
    const [, day, month, year] = dashedMatch;
    const normalizedMonth = normalizeMonthToken(month);
    return normalizedMonth ? `${String(Number(day)).padStart(2, '0')}-${normalizedMonth}-${year}` : raw;
  }

  const isoMatch = raw.match(ISO_DATE);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const normalizedMonth = MONTHS[Number(month) - 1];
    return normalizedMonth ? `${day}-${normalizedMonth}-${year}` : raw;
  }

  return raw;
};

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL + serviceURL + '/api' + PROJECT_OVERVIEW;
  } else {
    return process.env.REACT_APP_API_URL
      + `:${servicePort}`
      + serviceURL
      + '/api'
      + PROJECT_OVERVIEW;
  }
};

const baseURL = fetchBaseURL();
const tableScrollHeight = '100%';

const getProjectId = (rowData) => (
  rowData?.project_seq_id
  ?? rowData?.project?.project_seq_id
  ?? rowData?.project?.projectSeqId
  ?? rowData?.project_id
  ?? rowData?.project?.project_id
  ?? rowData?.project?.projectId
  ?? null
);

const addTemplateDefaults = (item = {}, menuItemLabel = '') => {
  if (item?.type !== 'filter') {
    return item;
  }

  const labelName = menuItemLabel
    || item.label
    || (item.field ? item.field.split('.').slice(-1)[0] : '')
    || 'Unknown';
  const normalizedItem = {
    ...item,
    label: item?.label || (item?.field ? `Filter by ${labelName}` : ''),
  };

  switch (item?.template) {
  case 'multiSelect':
  case 'choice':
    normalizedItem.selectedValues = Array.isArray(item?.selectedValues)
      ? item.selectedValues
      : [];
    break;
  case 'singleSelect':
    normalizedItem.selectedValues = Array.isArray(item?.selectedValues)
      ? item.selectedValues
      : [];
    normalizedItem.selectedValue = item?.selectedValue ?? null;
    normalizedItem.selectedLabel = item?.selectedLabel ?? '';
    break;
  case 'dateRange':
  case 'range':
    normalizedItem.range = Array.isArray(item?.range) && item.range.length >= 2
      ? [item.range[0] ?? null, item.range[1] ?? null]
      : [null, null];
    normalizedItem.selectedValues = Array.isArray(item?.selectedValues)
      ? item.selectedValues
      : [];
    break;
  case 'inputfield':
    normalizedItem.inputValue = item?.inputValue ?? '';
    break;
  case 'valueFilter':
  case 'numberFilter':
    normalizedItem.condition = item?.condition ?? null;
    normalizedItem.value = item?.value ?? null;
    normalizedItem.range = Array.isArray(item?.range) && item.range.length >= 2
      ? [item.range[0] ?? null, item.range[1] ?? null]
      : [null, null];
    break;
  default:
    break;
  }

  return normalizedItem;
};

const normalizeMenuItems = (menuItems = [], columnOptionsMap = {}) => (
  (Array.isArray(menuItems) ? menuItems : []).map((menuItem) => ({
    ...menuItem,
    items: Array.isArray(menuItem?.items)
      ? menuItem.items.map((item) => {
        const injectedItem = addTemplateDefaults(item, menuItem.label);
        if (injectedItem.type === 'filter' && columnOptionsMap[injectedItem.field]) {
          injectedItem.options = columnOptionsMap[injectedItem.field];
        }
        return injectedItem;
      })
      : menuItem?.items,
  }))
);

const normalizeColumns = (tableConfiguration = [], columnOptionsMap = {}) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration.map((column) => {
      const isFilterable = typeof column?.filter === 'boolean' ? column.filter : true;

      return {
        ...column,
        ...(column?.actions ? { actions: column.actions } : {}),
        filter: isFilterable,
        sortable: typeof column?.sortable === 'boolean' ? column.sortable : true,
        pinnable: typeof column?.pinnable === 'boolean' ? column.pinnable : true,
        filterVisible: typeof column?.filterVisible === 'boolean' ? column.filterVisible : true,
        showFilterButtonInHeader: typeof column?.showFilterButtonInHeader === 'boolean'
          ? column.showFilterButtonInHeader
          : isFilterable,
        showPinButtonInHeader: typeof column?.showPinButtonInHeader === 'boolean'
          ? column.showPinButtonInHeader
          : Boolean(column?.defaultPinned),
        menuItems: normalizeMenuItems(column?.menuItems, columnOptionsMap),
      };
    })
    : []
);

const toPositiveNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
};

const toNumberOrFallback = (value, fallback) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback;
};

const getMessageCount = (message) => {
  const [, count] = String(message || '').match(/^(\d+)\s+results?\s+found/i) || [];
  return toPositiveNumber(count);
};

const getPaginationCandidates = (response = {}) => ([
  response.meta,
  response.data?.meta,
  response.data?.data?.meta,
  response.pagination,
  response.paginationConfig,
  response.data?.pagination,
  response.data?.paginationConfig,
  response.data?.data?.pagination,
  response.data?.data?.paginationConfig,
].filter(Boolean));

const resolvePaginationMeta = (response, rawTableData) => {
  const paginationCandidates = getPaginationCandidates(response);
  const total = paginationCandidates
    .map((pagination) => toPositiveNumber(pagination?.total ?? pagination?.count))
    .find(Boolean)
    ?? toPositiveNumber(response?.total)
    ?? toPositiveNumber(response?.count)
    ?? toPositiveNumber(response?.data?.total)
    ?? toPositiveNumber(response?.data?.count)
    ?? toPositiveNumber(response?.data?.data?.total)
    ?? toPositiveNumber(response?.data?.data?.count)
    ?? getMessageCount(response?.message)
    ?? (Array.isArray(rawTableData) ? rawTableData.length : 0);
  const firstPagination = paginationCandidates[0] || {};

  return {
    ...(response.meta || {}),
    page: toNumberOrFallback(firstPagination?.page ?? firstPagination?.currentPage, 1),
    limit: toNumberOrFallback(firstPagination?.limit ?? firstPagination?.rows, 10),
    total,
  };
};

const getRawTableConfiguration = (response) => {
  const config = response.data?.tableConfiguration
    ?? response.data?.data?.tableConfiguration
    ?? response.tableConfiguration
    ?? response.tableConfig
    ?? response.data?.tableConfig
    ?? response.data?.data?.tableConfig;

  return Array.isArray(config) && config.length > 0 ? config : null;
};

const getRawTableData = (response) => {
  if (Array.isArray(response?.data?.tableData)) return response.data.tableData;
  if (Array.isArray(response?.data?.data?.tableData)) return response.data.data.tableData;
  if (Array.isArray(response?.tableData)) return response.tableData;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const normalizeProjectOverviewResponse = (response, columnOptionsMap = {}) => {
  if (!response || typeof response !== 'object') {
    return response;
  }

  const rawTableConfiguration = getRawTableConfiguration(response);
  const rawTableData = getRawTableData(response);

  const normalizeOverviewDateValues = (value) => {
    if (value === null || value === undefined) return value;
    if (Array.isArray(value)) {
      return value.map(normalizeOverviewDateValues);
    }

    if (typeof value === 'object') {
      return Object.entries(value).reduce((acc, [key, nestedValue]) => ({
        ...acc,
        [key]: normalizeOverviewDateValues(nestedValue),
      }), {});
    }

    return typeof value === 'string' ? normalizeOverviewDisplayDate(value) : value;
  };

  let tableConfiguration = null;
  if (rawTableConfiguration) {
    tableConfiguration = normalizeColumns(rawTableConfiguration, columnOptionsMap);
  }

  const resolvedMeta = resolvePaginationMeta(response, rawTableData);
  const tableData = Array.isArray(rawTableData)
    ? rawTableData.map(normalizeOverviewDateValues)
    : [];

  const isDataAnObject = response.data && typeof response.data === 'object' && !Array.isArray(response.data);

  response.meta = resolvedMeta;
  response.data = {
    ...(isDataAnObject ? response.data : {}),
    ...(tableConfiguration ? { tableConfiguration } : {}),
    tableData,
    meta: {
      ...(isDataAnObject ? response.data.meta : {}),
      ...resolvedMeta,
    },
  };

  response.success = typeof response.success === 'undefined' ? true : response.success;

  return response;
};

const ProjectOverview = () => {
  const { userProfile } = useAuth();

  const [srArchitects, setSrArchitects] = useState([]);
  const [architects, setArchitects] = useState([]);
  const additionalPayloadDataRef = useRef({});

  useEffect(() => {
    const fetchArchitects = async () => {
      try {
        const payload = { userInfo: userProfile };
        const resSr = await UseAxios(
          getArchitectNamesData(payload, 'sr-architect-names'),
          () => {},
        );
        const resAr = await UseAxios(
          getArchitectNamesData(payload),
          () => {},
        );
        if (resSr?.data?.data) setSrArchitects(resSr.data.data);
        if (resAr?.data?.data) setArchitects(resAr.data.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchArchitects();
  }, [userProfile]);

  const headersRef = useRef({ token: userProfile.access_token });
  headersRef.current.token = userProfile.access_token;

  const userInfoRef = useRef({
    emailId: userProfile.email,
    permissions: userProfile.permissions,
    roleDetails: userProfile.roleDetails,
    employeeId: userProfile.employeeId,
    globalColors: userProfile?.siteConfiguration?.global_colors,
  });
  userInfoRef.current.emailId = userProfile.email;
  userInfoRef.current.permissions = userProfile.permissions;
  userInfoRef.current.roleDetails = userProfile.roleDetails;
  userInfoRef.current.employeeId = userProfile.employeeId;
  userInfoRef.current.globalColors = userProfile?.siteConfiguration?.global_colors;

  const paginationConfigRef = useRef({
    page: 1,
    limit: 10,
    total: 0,
    rowsPerPageOptions: [10, 20, 30, 40],
  });

  const hasInitializedConfig = useRef(false);

  const handleCellClick = useCallback(({ fieldPath, rowData }) => {
    if (fieldPath !== 'project.projectName') return;

    const projectId = getProjectId(rowData);
    const query = new URLSearchParams();

    if (projectId) query.set('projectId', projectId);

    const accentColor = rowData?.project?.health
      || rowData?.project?.rag_status_color
      || rowData?.project?.ragStatusColor
      || rowData?.project?.overviewAccentColor
      || rowData?.project?.overview_accent_color
      || rowData?.health
      || rowData?.rag_status_color
      || rowData?.ragStatusColor
      || rowData?.overviewAccentColor
      || rowData?.overview_accent_color;

    if (projectId && accentColor) {
      try {
        localStorage.setItem(`project_accent_${projectId}`, accentColor);
      } catch (error) {
        console.warn('Failed to save accent color to localStorage', error);
      }
    }

    if (!query.toString()) return;

    window.open(`/delivery/project-details?${query.toString()}`, '_blank', 'noopener,noreferrer');
  }, []);

  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    additionalPayloadDataRef.current.isInitialLoad = false;

    const paginationData = response.meta || response.data?.meta || response.data?.data?.meta || {};
    const safePage = Number(paginationData.page) || 1;
    const safeLimit = Number(paginationData.limit || paginationData.pageSize) || 10;
    const safeTotal = Number(paginationData.total || paginationData.count) || 0;

    response.meta = {
      ...paginationData,
      page: safePage,
      limit: safeLimit,
      total: safeTotal,
    };

    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
      response.data.meta = response.meta;
      response.data.pagination = response.meta;
    }

    normalizeProjectOverviewResponse(response, {
      'architect.srArchitect': srArchitects,
      'architect.pArchitect': architects,
    });

    if (!hasInitializedConfig.current) {
      hasInitializedConfig.current = true;
    }
  }, []);

  const handlePaginationChange = useCallback((newPagination) => {
    paginationConfigRef.current.page = newPagination.page;
    paginationConfigRef.current.limit = newPagination.limit;
    paginationConfigRef.current.total = newPagination.total;
  }, []);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      exportHeaderUnmerge: true,
      isInitialLoad: true,
      additionalPayloadData: additionalPayloadDataRef.current,
      baseURL,
      userInfo: userInfoRef.current,
      apiEndpoints: {
        getAllViews: { endpoint: 'views', enabled: true },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: true },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('Project Details'),
          sheetName: () => getExportFileNameWithDate('Project Details'),
        },
      },
      handler: {
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse,
      },
      headers: headersRef.current,
      showToolBar: true,
      showPagination: true,
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showTieredMenu: true,
        showColumnCustomize: true,
        showAppliedFilters: true,
        showColumnFilterMenu: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: 'Project Overview',
        showSaveView: false,
        showReset: true,
        showExpandCollapse: true,
        additionalElements: [],
      },
    },
    tableSettings: {
      size: 'small',
      loaderSkeleton: {
        rows: 10,
        bodyCells: 2,
        columns: 5,
      },
      paginationConfig: {
        ...paginationConfigRef.current,
        onPaginationChange: handlePaginationChange,
        rowsPerPageOptions: [10, 20, 30, 40],
      },
      showExpandCollapse: true,
      tableScrollHeight,
      maxFrozenColumns: {
        desktop: 4,
        tablet: 2,
        mobile: 1,
      },
    },
  }), [
    handleCellClick,
    handleFilteredApiResponse,
    handlePaginationChange,
  ]);

  return (
    <div className="project-overview-outer-layer">
      <div className="project-overview">
        <div className="table-utility">
          <TableUtility configuration={configuration} />
        </div>
      </div>
    </div>
  );
};

export default ProjectOverview;
